import { Icon } from "@iconify/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { redirectAfterSignOut, signOutAdmin } from "../../api/adminAuth";
import { getUserAssociatedCompanies, switchCompany } from "../../api/Esop";
import optionsApi from "../../api/optionsApi";
import { OPERATIONAL_CONTROL_RESTRICTION_KEY } from "../../components/shared/OperationalControlAdmissionGate";
import { Label } from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import {
  ButtonSize,
  Center,
  PrimaryCTAButton,
  SecondaryCTAButton,
  VStack,
} from "../../components/utils";
import { useAuthStore } from "../../store";
import { isOperationalControlFeatureEnabled } from "../../utils/operationalControlFeature";

const restrictionMessage = () => {
  try {
    const rawRestriction = sessionStorage.getItem(
      OPERATIONAL_CONTROL_RESTRICTION_KEY
    );
    return rawRestriction
      ? (JSON.parse(rawRestriction).accessMessage as string | undefined)
      : undefined;
  } catch {
    return undefined;
  }
};

export default function AccessRestricted() {
  const operationalControlEnabled = isOperationalControlFeatureEnabled();
  const navigate = useNavigate();
  const customMessage = restrictionMessage();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const currentCompanyId = sessionStorage.getItem("companyId");
  const signOutInFlight = useRef(false);
  const [signingOut, setSigningOut] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [switchingCompany, setSwitchingCompany] = useState(false);
  const companies = useQuery(
    ["restricted-company-options"],
    getUserAssociatedCompanies,
    { enabled: operationalControlEnabled && isAuthenticated }
  );
  const availableCompanies = (companies.data?.associatedCompanies || []).filter(
    (company) => company.id !== currentCompanyId
  );
  const returnToSignIn = useCallback(async () => {
    if (signOutInFlight.current) return;
    signOutInFlight.current = true;
    setSigningOut(true);
    try {
      await signOutAdmin({}).catch(() => undefined);
    } finally {
      sessionStorage.removeItem(OPERATIONAL_CONTROL_RESTRICTION_KEY);
      redirectAfterSignOut();
    }
  }, []);
  const switchToCompany = async () => {
    if (!selectedCompanyId || switchingCompany) return;
    setSwitchingCompany(true);
    try {
      await switchCompany({ companyId: selectedCompanyId });
      sessionStorage.setItem("companyId", selectedCompanyId);
      sessionStorage.removeItem(OPERATIONAL_CONTROL_RESTRICTION_KEY);
      window.location.replace("/");
    } catch {
      toast.error("We couldn't switch companies. Try again.");
      setSwitchingCompany(false);
    }
  };

  useEffect(() => {
    if (!operationalControlEnabled) {
      sessionStorage.removeItem(OPERATIONAL_CONTROL_RESTRICTION_KEY);
      navigate("/", { replace: true });
      return undefined;
    }
    const rawRestriction = sessionStorage.getItem(
      OPERATIONAL_CONTROL_RESTRICTION_KEY
    );
    const restriction = rawRestriction ? JSON.parse(rawRestriction) : undefined;
    const expiresAt = restriction?.expiresAt as number | undefined;
    if (!expiresAt) return undefined;

    const remaining = Math.max(0, expiresAt - Date.now());
    const signOutTimer = window.setTimeout(returnToSignIn, remaining);
    const recheck = window.setInterval(async () => {
      try {
        const response = await optionsApi.get(
          "/v1/operational-controls/session-status"
        );
        if (response.data?.data?.incomingAccess !== "BLOCK") {
          sessionStorage.removeItem(OPERATIONAL_CONTROL_RESTRICTION_KEY);
          window.clearTimeout(signOutTimer);
          window.location.replace("/");
        }
      } catch {
        // The lockdown remains in place until its deadline or a successful
        // policy recheck confirms that access is restored.
      }
    }, 10_000);
    return () => {
      window.clearTimeout(signOutTimer);
      window.clearInterval(recheck);
    };
  }, [navigate, returnToSignIn, operationalControlEnabled]);

  if (!operationalControlEnabled) return null;

  return (
    <main className="min-h-screen bg-slate-light px-4">
      <Center className="min-h-screen">
        <VStack className="w-full max-w-lg items-center gap-5 rounded-lg border border-borderColor bg-white p-6 text-center shadow-card">
          <Icon
            aria-hidden="true"
            className="text-secondary"
            icon="lucide:shield-off"
            width={32}
          />
          <VStack className="items-center gap-2">
            <h1 className="text-xl font-semibold text-headerColor">
              Access temporarily restricted
            </h1>
            <p className="text-sm text-secondaryText">
              {customMessage ||
                "Access to this account has been restricted by your organisation. Please contact your company administrator if you need assistance."}
            </p>
          </VStack>
          {availableCompanies.length > 0 && (
            <VStack className="w-full gap-2 text-left">
              <Label htmlFor="restricted-company">Switch company</Label>
              <Select
                id="restricted-company"
                options={availableCompanies}
                value={selectedCompanyId}
                valueGetter={(company) => company.id}
                textGetter={(company) => company.name}
                placeholder="Choose another company"
                onChange={(event) => setSelectedCompanyId(event.target.value)}
              />
              <SecondaryCTAButton
                buttonSize={ButtonSize.MEDIUM}
                disabled={!selectedCompanyId || switchingCompany}
                onClick={switchToCompany}
              >
                {switchingCompany ? "Switching company" : "Switch company"}
              </SecondaryCTAButton>
            </VStack>
          )}
          {companies.isError && (
            <VStack className="items-center gap-2">
              <p className="text-sm text-secondaryText">
                We couldn&apos;t load your other companies.
              </p>
              <SecondaryCTAButton
                buttonSize={ButtonSize.MEDIUM}
                onClick={() => companies.refetch()}
              >
                Try again
              </SecondaryCTAButton>
            </VStack>
          )}
          <PrimaryCTAButton
            buttonSize={ButtonSize.MEDIUM}
            disabled={signingOut}
            onClick={returnToSignIn}
          >
            {signingOut ? "Logging out" : "Return to sign in"}
          </PrimaryCTAButton>
        </VStack>
      </Center>
    </main>
  );
}
